import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Modal, Radio, Select, Spin, Switch, Table, Tag, Popconfirm, Space } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DownloadOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  HolderOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { reorderListItems } from "../../../app/utils/common/reorderList";
import moment from "moment";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useSelector } from "react-redux";
import { protectedApi } from "../../../app/utils/common/protectedApi";
import {
  downloadBlobAsFile,
  downloadWrittenProjectDocAsPdf,
  sanitizeDocumentFilename,
} from "../../../app/utils/common/projectDocumentationDownload";
import {
  EXCEL_ACCEPT,
  PROJECT_DOC_UPLOAD_ACCEPT,
  fetchProjectDocumentationFileBlob,
  getProjectDocDownloadExtension,
  findProjectDocFileByName,
  getProjectDocReplaceAccept,
  getProjectDocReplaceUploadMode,
  getProjectDocUploadTagLabel,
  isProjectDocDocx,
  isProjectDocExcel,
  isProjectDocHtml,
  isProjectDocMarkdown,
  isProjectDocPdf,
  isProjectDocJson,
  isProjectDocTextPreview,
  validatePickedUploadFile,
  validatePickedUploadFiles,
} from "../../../app/utils/common/projectDocumentationFile";
import { APIS } from "../../../app/utils/constants/apiConstant";
import { checkPermission, permissionType, showErrorResponse, isAdminUser } from "../../../app/utils/common/Functions";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import CompanyPolicyPdfViewer from "../../molecules/CompanyPolicyPdfViewer/CompanyPolicyPdfViewer";
import ProjectDocumentationDocxViewer from "../../molecules/ProjectDocumentationDocxViewer/ProjectDocumentationDocxViewer";
import ProjectDocumentationExcelViewer from "../../molecules/ProjectDocumentationExcelViewer/ProjectDocumentationExcelViewer";
import ProjectDocumentationMarkdownViewer from "../../molecules/ProjectDocumentationMarkdownViewer/ProjectDocumentationMarkdownViewer";
import ProjectDocumentationTextViewer from "../../molecules/ProjectDocumentationTextViewer/ProjectDocumentationTextViewer";
import ProjectDocumentationHtmlViewer from "../../molecules/ProjectDocumentationHtmlViewer/ProjectDocumentationHtmlViewer";
import ProjectDocumentationBundledFilesView from "../../molecules/ProjectDocumentationBundledFilesView/ProjectDocumentationBundledFilesView";
import "./ProjectDocumentationPage.scss";

const DocListPdfPreview = ({ documentId, onLoadError }) => {
  const [url, setUrl] = useState(null);
  const [innerLoading, setInnerLoading] = useState(true);
  const blobRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (blobRef.current) {
      URL.revokeObjectURL(blobRef.current);
      blobRef.current = null;
    }
    setInnerLoading(true);
    setUrl(null);
    (async () => {
      try {
        const blob = await fetchProjectDocumentationFileBlob(documentId);
        const nextUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(nextUrl);
          return;
        }
        blobRef.current = nextUrl;
        setUrl(nextUrl);
        setInnerLoading(false);
      } catch (e) {
        if (!cancelled) {
          setInnerLoading(false);
          onLoadError(e);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
    };
  }, [documentId, onLoadError]);

  if (innerLoading && !url) {
    return (
      <div className="flex justify-center py-8">
        <Spin />
      </div>
    );
  }

  if (!url) return null;

  return (
    <div
      className="project-doc-preview-inline max-h-[min(50vh,420px)] overflow-y-auto bg-[#f5f5f5] rounded-[8px] p-2"
      onDragStart={(e) => e.preventDefault()}
    >
      <CompanyPolicyPdfViewer fileUrl={url} onLoadError={onLoadError} />
    </div>
  );
};

const MAIN_TAB_GENERAL = "general";
const MAIN_TAB_ALL = "all";
const MAIN_TAB_ACCESS_REQUESTS = "access-requests";

const DocListExcelPreview = ({ documentId, onLoadError }) => (
  <div className="project-doc-preview-inline max-h-[min(50vh,420px)] overflow-y-auto bg-[#f5f5f5] rounded-[8px] p-2">
    <ProjectDocumentationExcelViewer documentId={documentId} onLoadError={onLoadError} compact />
  </div>
);

const DocListDocxPreview = ({ documentId, onLoadError }) => (
  <div className="project-doc-preview-inline max-h-[min(50vh,420px)] overflow-y-auto bg-[#f5f5f5] rounded-[8px] p-2">
    <ProjectDocumentationDocxViewer documentId={documentId} onLoadError={onLoadError} compact />
  </div>
);

const DocListMarkdownPreview = ({ documentId, onLoadError }) => (
  <div className="project-doc-preview-inline max-h-[min(50vh,420px)] overflow-y-auto bg-[#f5f5f5] rounded-[8px] p-2">
    <ProjectDocumentationMarkdownViewer documentId={documentId} onLoadError={onLoadError} compact />
  </div>
);

const DocListHtmlPreview = ({ documentId, onLoadError }) => (
  <div className="project-doc-preview-inline max-h-[min(50vh,420px)] overflow-y-auto bg-[#f5f5f5] rounded-[8px] p-2">
    <ProjectDocumentationHtmlViewer documentId={documentId} onLoadError={onLoadError} compact />
  </div>
);

const DocListTextPreview = ({ documentId, doc, onLoadError }) => (
  <div className="project-doc-preview-inline max-h-[min(50vh,420px)] overflow-y-auto bg-[#f5f5f5] rounded-[8px] p-2">
    <ProjectDocumentationTextViewer
      documentId={documentId}
      onLoadError={onLoadError}
      compact
      formatJson={isProjectDocJson(doc)}
    />
  </div>
);

const canPreviewProjectDocInList = (doc) =>
  Boolean(
    doc?.hasFile &&
    !doc?.hasMultipleFiles &&
    (isProjectDocPdf(doc) ||
      isProjectDocExcel(doc) ||
      isProjectDocDocx(doc) ||
      isProjectDocMarkdown(doc) ||
      isProjectDocHtml(doc) ||
      isProjectDocTextPreview(doc))
  );

const ProjectDocumentationPage = () => {
  const { user } = useSelector((state) => state.auth.auth);
  const isAdmin = isAdminUser(user);
  const canUseProjectFilter =
    isAdmin || checkPermission(permissionType.PROJECT_DOCUMENTATION, "project filter");
  const canUseEmployeeFilter =
    isAdmin || checkPermission(permissionType.PROJECT_DOCUMENTATION, "employee filter");
  const canProjectDocUpdate =
    isAdmin || checkPermission(permissionType.PROJECT_DOCUMENTATION, "update");
  /** Anyone who can create docs may request edit access on their own documents (same as backend). */
  const canSubmitProjectDocEditRequest =
    isAdmin ||
    checkPermission(permissionType.PROJECT_DOCUMENTATION, "request edit") ||
    checkPermission(permissionType.PROJECT_DOCUMENTATION, "create");
  const canReviewEditRequests = canProjectDocUpdate;
  const canCreateProjectDoc =
    isAdmin || checkPermission(permissionType.PROJECT_DOCUMENTATION, "create");
  const canShareProjectDoc =
    isAdmin || checkPermission(permissionType.PROJECT_DOCUMENTATION, "share document");

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [title, setTitle] = useState("");
  const [projectName, setProjectName] = useState("");
  const [selectedProjectFilter, setSelectedProjectFilter] = useState(undefined);
  const [projectOptions, setProjectOptions] = useState([]);
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState(undefined);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [content, setContent] = useState("");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [isEditorExpanded, setIsEditorExpanded] = useState(false);
  /** 'written' | 'upload' | 'excel' */
  const [docInputMode, setDocInputMode] = useState("written");
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [attachmentDragFrom, setAttachmentDragFrom] = useState(null);
  const [attachmentDragOver, setAttachmentDragOver] = useState(null);
  const attachmentInputRef = useRef(null);

  const ATTACHMENT_DRAG_INDEX = "application/x-project-doc-attachment-index";

  const [listPreviewId, setListPreviewId] = useState(null);
  const [modalPdfUrl, setModalPdfUrl] = useState(null);
  const [modalPdfLoading, setModalPdfLoading] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [isReorderingFiles, setIsReorderingFiles] = useState(false);
  const reorderingInFlightRef = useRef(false);
  const [publicToggleLoadingId, setPublicToggleLoadingId] = useState(null);

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareModalDoc, setShareModalDoc] = useState(null);
  const [shareModalLoading, setShareModalLoading] = useState(false);
  const [shareSaving, setShareSaving] = useState(false);
  const [shareEmployeeOptions, setShareEmployeeOptions] = useState([]);
  const [shareSelectedUserIds, setShareSelectedUserIds] = useState([]);

  const [pendingEditRequests, setPendingEditRequests] = useState([]);
  const [pendingEditLoading, setPendingEditLoading] = useState(false);
  const [editProjectName, setEditProjectName] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [replaceAttachmentFile, setReplaceAttachmentFile] = useState(null);
  const [editReplaceHint, setEditReplaceHint] = useState("");
  const replaceAttachmentInputRef = useRef(null);
  const [editSaving, setEditSaving] = useState(false);
  const [requestingEdit, setRequestingEdit] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);

  /** Separate from View: edit document (forms) */
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editModalPdfUrl, setEditModalPdfUrl] = useState(null);
  const [editModalPdfLoading, setEditModalPdfLoading] = useState(false);

  /** Author needs approval — informational + optional submit request */
  const [editGuidanceOpen, setEditGuidanceOpen] = useState(false);
  const [editGuidanceDoc, setEditGuidanceDoc] = useState(null);
  const [activeMainTab, setActiveMainTab] = useState(MAIN_TAB_ALL);

  const viewerId = Number(user?.id ?? user?.userId);
  const showAccessRequestsTab = canReviewEditRequests;

  const isDocAuthor = useCallback(
    (doc) => {
      if (doc == null || !Number.isFinite(viewerId)) return false;
      if (doc.authorId != null) return viewerId === Number(doc.authorId);
      return activeMainTab === MAIN_TAB_GENERAL;
    },
    [viewerId, activeMainTab]
  );

  const listRowShowsEditButton = useCallback(
    (doc) =>
      Boolean(doc) && (isAdmin || canProjectDocUpdate || (isDocAuthor(doc) && canSubmitProjectDocEditRequest)),
    [isAdmin, canProjectDocUpdate, isDocAuthor, canSubmitProjectDocEditRequest]
  );

  const listRowCanOpenEditModal = useCallback(
    (doc) => {
      if (!doc) return false;
      if (isAdmin || canProjectDocUpdate) return true;
      return Boolean(isDocAuthor(doc) && canSubmitProjectDocEditRequest && doc.editAccessStatus === "approved");
    },
    [isAdmin, canProjectDocUpdate, isDocAuthor, canSubmitProjectDocEditRequest]
  );

  const onListPdfError = useCallback(() => {
    showErrorToast("Could not load the PDF preview.");
  }, []);

  const onListExcelError = useCallback(() => {
    showErrorToast("Could not load the Excel preview.");
  }, []);

  const onListDocxError = useCallback(() => {
    showErrorToast("Could not load the Word document preview.");
  }, []);

  const onListMarkdownError = useCallback(() => {
    showErrorToast("Could not load the Markdown preview.");
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const response = await protectedApi({
        url: APIS.PROJECT_DOCUMENTS_PROJECTS,
        method: "GET",
      });
      const projects = Array.isArray(response?.data?.data) ? response.data.data : [];
      setProjectOptions(projects.map((name) => ({ label: name, value: name })));
    } catch (error) {
      showErrorResponse(error);
    }
  }, []);

  const loadEmployees = useCallback(async () => {
    try {
      const response = await protectedApi({
        url: APIS.PROJECT_DOCUMENTS_EMPLOYEES,
        method: "GET",
      });
      const employees = Array.isArray(response?.data?.data) ? response.data.data : [];
      setEmployeeOptions(
        employees.map((emp) => ({
          label: emp.fullName,
          value: emp.userId,
        }))
      );
    } catch (error) {
      showErrorResponse(error);
    }
  }, []);

  const loadShareEmployeeOptions = useCallback(async () => {
    try {
      const paths = [APIS.EMPLOYEE_DIRECTORY, APIS.EMPLOYEE_DIRECTORY_FALLBACK];
      for (const url of paths) {
        try {
          const response = await protectedApi({ url, method: "GET" });
          const users = response?.data?.data?.users || response?.data?.data || [];
          setShareEmployeeOptions(
            (Array.isArray(users) ? users : []).map((employee) => {
              const id = Number(employee.userId || employee.id);
              const name = `${employee.firstname || ""} ${employee.lastname || ""}`.trim();
              return {
                value: id,
                label: name || employee.email || `User #${id}`,
              };
            }).filter((option) => option.value > 0)
          );
          return;
        } catch (error) {
          if (error?.response?.status !== 404) break;
        }
      }
      setShareEmployeeOptions([]);
    } catch (error) {
      showErrorResponse(error);
    }
  }, []);

  const emitPendingEditCount = useCallback((count) => {
    window.dispatchEvent(
      new CustomEvent("project-documentation-pending-count", { detail: { count: Number(count) || 0 } })
    );
  }, []);

  const loadPendingEditRequests = useCallback(async () => {
    if (!canReviewEditRequests) {
      emitPendingEditCount(0);
      return;
    }
    setPendingEditLoading(true);
    try {
      const response = await protectedApi({
        url: APIS.PROJECT_DOCUMENTATION_EDIT_REQUESTS_PENDING,
        method: "GET",
      });
      const list = Array.isArray(response?.data?.data) ? response.data.data : [];
      setPendingEditRequests(list);
      emitPendingEditCount(list.length);
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setPendingEditLoading(false);
    }
  }, [canReviewEditRequests, emitPendingEditCount]);

  const loadMyDocs = useCallback(
    async (projectFilter, employeeFilter, mainTab) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        const effectiveProjectFilter =
          mainTab === MAIN_TAB_GENERAL ? undefined : projectFilter;
        if (effectiveProjectFilter) params.set("projectName", effectiveProjectFilter);

        let endpoint = APIS.PROJECT_DOCUMENTS_MINE;

        if (mainTab === MAIN_TAB_GENERAL) {
          params.set("scope", "general");
        } else if (mainTab === MAIN_TAB_ALL) {
          if (canUseEmployeeFilter && employeeFilter) {
            endpoint = APIS.PROJECT_DOCUMENTS_BY_EMPLOYEE;
            params.set("employeeUserId", String(employeeFilter));
          } else if (isAdmin) {
            params.set("scope", "all");
          } else {
            params.set("scope", "mine");
          }
        } else {
          setDocs([]);
          setLoading(false);
          return;
        }

        const query = params.toString() ? `?${params.toString()}` : "";
        const response = await protectedApi({
          url: `${endpoint}${query}`,
          method: "GET",
        });
        setDocs(Array.isArray(response?.data?.data) ? response.data.data : []);
      } catch (error) {
        showErrorResponse(error);
      } finally {
        setLoading(false);
      }
    },
    [canUseEmployeeFilter, isAdmin]
  );

  const handleMainTabChange = (key) => {
    setActiveMainTab(key);
    setListPreviewId(null);
    if (key === MAIN_TAB_GENERAL) {
      setSelectedEmployeeFilter(undefined);
      setSelectedProjectFilter(undefined);
    }
  };

  useEffect(() => {
    if (!canUseProjectFilter) return;
    loadProjects();
  }, [loadProjects, canUseProjectFilter]);

  useEffect(() => {
    if (!canUseEmployeeFilter) return;
    loadEmployees();
  }, [loadEmployees, canUseEmployeeFilter]);

  useEffect(() => {
    if (!canShareProjectDoc) return;
    loadShareEmployeeOptions();
  }, [canShareProjectDoc, loadShareEmployeeOptions]);

  useEffect(() => {
    if (activeMainTab === MAIN_TAB_ACCESS_REQUESTS) return;
    loadMyDocs(selectedProjectFilter, selectedEmployeeFilter, activeMainTab);
  }, [loadMyDocs, selectedProjectFilter, selectedEmployeeFilter, activeMainTab]);

  useEffect(() => {
    loadPendingEditRequests();
  }, [loadPendingEditRequests]);

  useEffect(() => {
    if (!editModalOpen || !editingDoc) return;
    setEditProjectName(String(editingDoc.projectName || ""));
    setEditTitle(String(editingDoc.title || ""));
    setEditContent(String(editingDoc.content || ""));
    setReplaceAttachmentFile(null);
    setEditReplaceHint("");
    if (replaceAttachmentInputRef.current) replaceAttachmentInputRef.current.value = "";
  }, [editModalOpen, editingDoc?.id]);

  useEffect(() => {
    if (!editModalOpen || !editingDoc?.hasFile || !isProjectDocPdf(editingDoc)) {
      setEditModalPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setEditModalPdfLoading(false);
      return undefined;
    }

    let cancelled = false;
    let blobUrl = null;
    setEditModalPdfLoading(true);
    setEditModalPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    (async () => {
      try {
        const blob = await fetchProjectDocumentationFileBlob(editingDoc.id);
        blobUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setEditModalPdfUrl(blobUrl);
        }
      } catch (e) {
        if (!cancelled) {
          showErrorResponse(e);
        }
      } finally {
        if (!cancelled) setEditModalPdfLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [editModalOpen, editingDoc?.id, editingDoc?.hasFile, editingDoc?.mimeType, editingDoc?.fileName]);

  useEffect(() => {
    if (!isEditorExpanded) return;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = oldOverflow;
    };
  }, [isEditorExpanded]);

  useEffect(() => {
    if (
      !viewOpen ||
      !selectedDoc?.hasFile ||
      selectedDoc?.hasMultipleFiles ||
      !isProjectDocPdf(selectedDoc)
    ) {
      setModalPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setModalPdfLoading(false);
      return undefined;
    }

    let cancelled = false;
    let blobUrl = null;
    setModalPdfLoading(true);
    setModalPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    (async () => {
      try {
        const blob = await fetchProjectDocumentationFileBlob(selectedDoc.id);
        blobUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setModalPdfUrl(blobUrl);
        }
      } catch (e) {
        if (!cancelled) {
          showErrorResponse(e);
        }
      } finally {
        if (!cancelled) setModalPdfLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [viewOpen, selectedDoc?.id, selectedDoc?.hasFile, selectedDoc?.mimeType, selectedDoc?.fileName]);

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link"],
      ["clean"],
    ],
  };

  const getTextFromHtml = (html) =>
    String(html || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .trim();

  const clearAttachmentInput = () => {
    setAttachmentFiles([]);
    setAttachmentDragFrom(null);
    setAttachmentDragOver(null);
    if (attachmentInputRef.current) attachmentInputRef.current.value = "";
  };

  const onPickAttachment = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) {
      setAttachmentFiles([]);
      return;
    }
    const mode = docInputMode === "excel" ? "excel" : "upload";
    const check = validatePickedUploadFiles(picked, mode);
    if (!check.ok) {
      showErrorToast(check.message);
      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
      setAttachmentFiles([]);
      return;
    }
    setAttachmentFiles(check.files);
  };

  const moveAttachmentFile = (index, direction) => {
    setAttachmentFiles((prev) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;
      return reorderListItems(prev, index, targetIndex);
    });
  };

  const reorderAttachmentFile = (fromIndex, toIndex) => {
    setAttachmentFiles((prev) => reorderListItems(prev, fromIndex, toIndex));
  };

  const closeCreateForm = () => {
    setShowCreateForm(false);
    setIsEditorExpanded(false);
    setDocInputMode("written");
    setProjectName("");
    setTitle("");
    setContent("");
    clearAttachmentInput();
  };

  const resetFormAfterSuccess = async () => {
    closeCreateForm();
    await loadMyDocs(selectedProjectFilter, selectedEmployeeFilter, activeMainTab);
    if (canUseProjectFilter) {
      await loadProjects();
    }
    if (canUseEmployeeFilter) {
      await loadEmployees();
    }
  };

  const onSubmitDocument = async () => {
    const safeProjectName = String(projectName || "").trim();
    const safeTitle = String(title || "").trim();

    if (safeProjectName.length < 2) {
      showErrorToast("Please add project name.");
      return;
    }
    if (safeTitle.length < 2) {
      showErrorToast("Please add document title.");
      return;
    }

    if (docInputMode === "written") {
      const safeContent = String(content || "");
      const safeContentText = getTextFromHtml(safeContent);
      if (!safeContentText.length) {
        showErrorToast("Document content is required.");
        return;
      }

      setSaving(true);
      try {
        const response = await protectedApi({
          url: APIS.PROJECT_DOCUMENTS_CREATE,
          method: "POST",
          data: {
            projectName: safeProjectName,
            title: safeTitle,
            content: safeContent,
          },
        });
        showSuccessToast(response?.data?.message || "Project documentation submitted.");
        await resetFormAfterSuccess();
      } catch (error) {
        showErrorResponse(error);
      } finally {
        setSaving(false);
      }
      return;
    }

    const uploadMode = docInputMode === "excel" ? "excel" : "upload";
    const fileCheck = validatePickedUploadFiles(attachmentFiles, uploadMode);
    if (!fileCheck.ok) {
      showErrorToast(fileCheck.message);
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      fileCheck.files.forEach((file) => formData.append("files", file));
      formData.append("projectName", safeProjectName);
      formData.append("title", safeTitle);
      const response = await protectedApi({
        url: APIS.PROJECT_DOCUMENTS_CREATE_WITH_FILE,
        method: "POST",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        _dataEncrypted: true,
      });
      const count = response?.data?.data?.count ?? fileCheck.files.length;
      showSuccessToast(
        response?.data?.message ||
        (count === 1
          ? docInputMode === "excel"
            ? "Excel sheet uploaded."
            : "File uploaded."
          : `${count} files uploaded.`)
      );
      await resetFormAfterSuccess();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setSaving(false);
    }
  };

  const openDoc = async (id) => {
    setViewOpen(true);
    setViewLoading(true);
    try {
      const response = await protectedApi({
        url: APIS.PROJECT_DOCUMENT_BY_ID.replace(":documentId", id),
        method: "GET",
      });
      setSelectedDoc(response?.data?.data || null);
    } catch (error) {
      showErrorResponse(error);
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const openEditModal = async (id) => {
    setEditModalOpen(true);
    setEditModalLoading(true);
    try {
      const response = await protectedApi({
        url: APIS.PROJECT_DOCUMENT_BY_ID.replace(":documentId", id),
        method: "GET",
      });
      setEditingDoc(response?.data?.data || null);
    } catch (error) {
      showErrorResponse(error);
      setEditModalOpen(false);
      setEditingDoc(null);
    } finally {
      setEditModalLoading(false);
    }
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingDoc(null);
    setReplaceAttachmentFile(null);
    setEditReplaceHint("");
    if (replaceAttachmentInputRef.current) replaceAttachmentInputRef.current.value = "";
    setEditModalPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setEditModalPdfLoading(false);
  };

  const handleListEditClick = (doc) => {
    if (listRowCanOpenEditModal(doc)) {
      openEditModal(doc.id);
      return;
    }
    if (isDocAuthor(doc) && canSubmitProjectDocEditRequest && !canProjectDocUpdate) {
      setEditGuidanceDoc(doc);
      setEditGuidanceOpen(true);
    }
  };

  const closeModal = () => {
    reorderingInFlightRef.current = false;
    setIsReorderingFiles(false);
    setViewOpen(false);
    setSelectedDoc(null);
  };

  const saveWrittenEdits = async () => {
    if (!editingDoc?.id) return;
    const safeProject = String(editProjectName || "").trim();
    const safeTitle = String(editTitle || "").trim();
    const safeContent = String(editContent || "");
    if (safeProject.length < 2 || safeTitle.length < 2) {
      showErrorToast("Project name and title are required.");
      return;
    }
    if (!getTextFromHtml(safeContent).length) {
      showErrorToast("Document content is required.");
      return;
    }
    setEditSaving(true);
    try {
      const docId = editingDoc.id;
      const response = await protectedApi({
        url: APIS.PROJECT_DOCUMENTATION_UPDATE.replace(":documentId", String(docId)),
        method: "PATCH",
        data: { projectName: safeProject, title: safeTitle, content: safeContent },
      });
      showSuccessToast(response?.data?.message || "Document updated.");
      closeEditModal();
      await loadMyDocs(selectedProjectFilter, selectedEmployeeFilter, activeMainTab);
      await loadPendingEditRequests();
      if (viewOpen && Number(selectedDoc?.id) === Number(docId)) {
        await openDoc(docId);
      }
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setEditSaving(false);
    }
  };

  const saveFileReplace = async () => {
    const replaceMode = getProjectDocReplaceUploadMode(editingDoc);
    const fileCheck = validatePickedUploadFile(replaceAttachmentFile, replaceMode);
    if (!editingDoc?.id || !fileCheck.ok) {
      showErrorToast(fileCheck.message);
      return;
    }
    const safeProject = String(editProjectName || "").trim();
    const safeTitle = String(editTitle || "").trim();
    if (safeProject.length < 2 || safeTitle.length < 2) {
      showErrorToast("Project name and title are required.");
      return;
    }
    setEditSaving(true);
    try {
      const docId = editingDoc.id;
      const formData = new FormData();
      formData.append("file", replaceAttachmentFile);
      formData.append("projectName", safeProject);
      formData.append("title", safeTitle);
      const response = await protectedApi({
        url: APIS.PROJECT_DOCUMENTATION_UPDATE_FILE.replace(":documentId", String(docId)),
        method: "PATCH",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        _dataEncrypted: true,
      });
      showSuccessToast(response?.data?.message || "File updated.");
      setReplaceAttachmentFile(null);
      setEditReplaceHint("");
      if (replaceAttachmentInputRef.current) replaceAttachmentInputRef.current.value = "";
      closeEditModal();
      await loadMyDocs(selectedProjectFilter, selectedEmployeeFilter, activeMainTab);
      await loadPendingEditRequests();
      if (viewOpen && Number(selectedDoc?.id) === Number(docId)) {
        await openDoc(docId);
      }
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setEditSaving(false);
    }
  };

  const requestEditFromGuidanceModal = async () => {
    const id = editGuidanceDoc?.id;
    if (!id) return;
    setRequestingEdit(true);
    try {
      const response = await protectedApi({
        url: APIS.PROJECT_DOCUMENTATION_EDIT_REQUEST.replace(":documentId", String(id)),
        method: "POST",
      });
      showSuccessToast(response?.data?.message || "Request submitted.");
      setEditGuidanceOpen(false);
      setEditGuidanceDoc(null);
      await loadMyDocs(selectedProjectFilter, selectedEmployeeFilter, activeMainTab);
      await loadPendingEditRequests();
      if (viewOpen && Number(selectedDoc?.id) === Number(id)) {
        await openDoc(id);
      }
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setRequestingEdit(false);
    }
  };

  const reviewEditRequest = async (requestId, status) => {
    setReviewingId(requestId);
    const viewDocId = selectedDoc?.id;
    const wasEditOpen = editModalOpen;
    const editDocId = editingDoc?.id;
    try {
      const response = await protectedApi({
        url: APIS.PROJECT_DOCUMENTATION_EDIT_REQUEST_REVIEW.replace(":requestId", String(requestId)),
        method: "POST",
        data: { status },
      });
      showSuccessToast(response?.data?.message || (status === "approved" ? "Approved." : "Rejected."));
      await loadPendingEditRequests();
      await loadMyDocs(selectedProjectFilter, selectedEmployeeFilter, activeMainTab);
      if (viewOpen && viewDocId) {
        await openDoc(viewDocId);
      }
      if (wasEditOpen && editDocId) {
        await openEditModal(editDocId);
      }
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setReviewingId(null);
    }
  };

  const toggleDocumentPublic = async (doc, nextPublic) => {
    if (!doc?.id) return;
    setPublicToggleLoadingId(doc.id);
    try {
      const response = await protectedApi({
        url: APIS.PROJECT_DOCUMENTATION_PUBLIC.replace(":documentId", String(doc.id)),
        method: "PATCH",
        data: { isPublic: nextPublic },
      });
      showSuccessToast(
        response?.data?.message ||
        (nextPublic
          ? "Document is now visible in everyone's General doc list."
          : "Document removed from the shared General doc list.")
      );
      await loadMyDocs(selectedProjectFilter, selectedEmployeeFilter, activeMainTab);
      if (viewOpen && Number(selectedDoc?.id) === Number(doc.id)) {
        await openDoc(doc.id);
      }
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setPublicToggleLoadingId(null);
    }
  };

  const openShareModal = async (doc) => {
    if (!doc?.id || !canShareProjectDoc) return;
    setShareModalDoc(doc);
    setShareModalOpen(true);
    setShareModalLoading(true);
    setShareSelectedUserIds([]);
    try {
      const response = await protectedApi({
        url: APIS.PROJECT_DOCUMENTATION_SHARES.replace(":documentId", String(doc.id)),
        method: "GET",
      });
      const sharedUsers = Array.isArray(response?.data?.data) ? response.data.data : [];
      setShareSelectedUserIds(sharedUsers.map((row) => Number(row.userId)).filter((id) => id > 0));
    } catch (error) {
      showErrorResponse(error);
      setShareModalOpen(false);
      setShareModalDoc(null);
    } finally {
      setShareModalLoading(false);
    }
  };

  const closeShareModal = () => {
    setShareModalOpen(false);
    setShareModalDoc(null);
    setShareSelectedUserIds([]);
    setShareModalLoading(false);
  };

  const saveDocumentShares = async () => {
    if (!shareModalDoc?.id) return;
    setShareSaving(true);
    try {
      const response = await protectedApi({
        url: APIS.PROJECT_DOCUMENTATION_SHARES.replace(":documentId", String(shareModalDoc.id)),
        method: "PATCH",
        data: { userIds: shareSelectedUserIds },
      });
      showSuccessToast(response?.data?.message || "Document sharing updated.");
      closeShareModal();
      await loadMyDocs(selectedProjectFilter, selectedEmployeeFilter, activeMainTab);
      if (viewOpen && Number(selectedDoc?.id) === Number(shareModalDoc.id)) {
        await openDoc(shareModalDoc.id);
      }
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setShareSaving(false);
    }
  };

  const downloadProjectDocFile = async (parentDocId, fileMeta, titleFallback) => {
    const blob = await fetchProjectDocumentationFileBlob(parentDocId, fileMeta.id);
    const base = sanitizeDocumentFilename(titleFallback || "document");
    const ext = getProjectDocDownloadExtension(fileMeta);
    const name = fileMeta.fileName || `${base}${ext}`;
    downloadBlobAsFile(blob, name.includes(".") ? name : `${base}${ext}`);
  };

  const onDownloadBundledFile = async (parentDocId, fileMeta) => {
    setDownloadingFileId(fileMeta.id);
    try {
      await downloadProjectDocFile(parentDocId, fileMeta, selectedDoc?.title);
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setDownloadingFileId(null);
    }
  };

  const onReorderBundledFiles = async (_parentDocId, orderedFiles) => {
    if (reorderingInFlightRef.current) return;
    if (!selectedDoc?.id || !Array.isArray(orderedFiles) || orderedFiles.length < 2) return;
    const fileIds = orderedFiles.map((f) => f.id).filter(Boolean);
    if (fileIds.length < 2) return;

    reorderingInFlightRef.current = true;
    setIsReorderingFiles(true);
    try {
      const response = await protectedApi({
        url: APIS.PROJECT_DOCUMENTATION_REORDER_FILES.replace(":documentId", String(selectedDoc.id)),
        method: "PATCH",
        data: { fileIds },
      });
      const nextFiles = response?.data?.data?.files;
      setSelectedDoc((prev) =>
        prev
          ? {
            ...prev,
            files: Array.isArray(nextFiles) ? nextFiles : prev.files,
          }
          : prev
      );
      showSuccessToast(response?.data?.message || "File order updated.");
      await loadMyDocs(selectedProjectFilter, selectedEmployeeFilter, activeMainTab);
    } catch (error) {
      showErrorResponse(error);
    } finally {
      reorderingInFlightRef.current = false;
      setIsReorderingFiles(false);
    }
  };

  const onDownloadDocument = async (doc) => {
    setDownloadingDocId(doc.id);
    try {
      if (doc.hasFile) {
        if (doc.hasMultipleFiles) {
          let files = Array.isArray(doc.files) ? doc.files : [];
          if (!files.length) {
            const response = await protectedApi({
              url: APIS.PROJECT_DOCUMENT_BY_ID.replace(":documentId", String(doc.id)),
              method: "GET",
            });
            files = response?.data?.data?.files || [];
          }
          if (!files.length) {
            showErrorToast("No files found for this document.");
            return;
          }
          for (const file of files) {
            await downloadProjectDocFile(doc.id, file, doc.title);
          }
          showSuccessToast(
            files.length === 1 ? "File downloaded." : `${files.length} files downloaded.`
          );
          return;
        }
        const blob = await fetchProjectDocumentationFileBlob(doc.id);
        const base = sanitizeDocumentFilename(doc.title);
        const ext = getProjectDocDownloadExtension(doc);
        const fallbackName = doc.fileName || `${base}${ext}`;
        downloadBlobAsFile(blob, fallbackName.includes(".") ? fallbackName : `${base}${ext}`);
        return;
      }
      const response = await protectedApi({
        url: APIS.PROJECT_DOCUMENT_BY_ID.replace(":documentId", doc.id),
        method: "GET",
      });
      const full = response?.data?.data;
      const bodyHtml = String(full?.content || "").trim();
      if (!bodyHtml) {
        showErrorToast("This document has no content to export.");
        return;
      }
      await downloadWrittenProjectDocAsPdf({
        title: full?.title || doc.title,
        projectName: full?.projectName || doc.projectName,
        html: full.content,
      });
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setDownloadingDocId(null);
    }
  };

  const showAuthorForDoc = useCallback(
    (doc) =>
      isAdmin ||
      activeMainTab === MAIN_TAB_ALL ||
      Boolean(doc?.sharedWithMe) ||
      (activeMainTab === MAIN_TAB_GENERAL && Boolean(doc?.isPublic) && !isDocAuthor(doc)),
    [isAdmin, activeMainTab, isDocAuthor]
  );

  const renderAccessRequestsPanel = () => (
    <div
      className={`project-doc-pending-panel max-w-full rounded-[14px] p-6 border-2 shadow-md transition-colors ${pendingEditRequests.length > 0
        ? "border-[#ff4d4f] bg-[#fff2f0] shadow-[0_4px_24px_rgba(255,77,79,0.18)] ring-1 ring-[#ffccc7]"
        : "border-[#e7ebf6] border-solid bg-white shadow-sm"
        }`}
    >
      <p className="text-sm text-gray-600 mb-4">
        Authors with create (or request edit) permission can ask to edit their documents. Approve or reject requests
        here.
      </p>
      <div className="project-doc-table-wrap">
        <Table
          size="small"
          rowKey="requestId"
          loading={pendingEditLoading}
          dataSource={pendingEditRequests}
          pagination={false}
          scroll={{ x: 720 }}
          locale={{ emptyText: "No pending requests." }}
          columns={[
            { title: "Project", dataIndex: "projectName", key: "projectName" },
            { title: "Document", dataIndex: "documentTitle", key: "documentTitle" },
            { title: "Requested by", dataIndex: "requesterName", key: "requesterName" },
            {
              title: "Requested",
              dataIndex: "createdAt",
              key: "createdAt",
              render: (t) => (t ? moment(t).format("D MMM YYYY, h:mm A") : "-"),
            },
            {
              title: "Actions",
              key: "actions",
              width: 220,
              render: (_, row) => (
                <Space size="small" wrap>
                  <Popconfirm
                    title="Approve edit access for this document?"
                    okText="Approve"
                    cancelText="Cancel"
                    onConfirm={() => reviewEditRequest(row.requestId, "approved")}
                  >
                    <Button type="primary" size="small" loading={reviewingId === row.requestId}>
                      Approve
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title="Reject this edit access request?"
                    okText="Reject"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => reviewEditRequest(row.requestId, "rejected")}
                  >
                    <Button danger size="small" loading={reviewingId === row.requestId}>
                      Reject
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </div>
    </div>
  );

  const renderDocListPanel = () => {
    const isGeneralTab = activeMainTab === MAIN_TAB_GENERAL;
    const listTitle = isGeneralTab ? "General documents" : "All documents";
    const listSubtitle = isGeneralTab
      ? "Documents an admin has shared for everyone"
      : isAdmin
        ? "Browse all employee documentation including Excel sheets and docs from ex-employees. Use the employee filter to view one person’s docs."
        : "Your own project documentation, plus any documents shared with you directly.";
    const visibleDocs = isGeneralTab ? docs.filter((doc) => !isProjectDocExcel(doc)) : docs;
    const showProjectFilter = !isGeneralTab && canUseProjectFilter;
    const emptyText = isGeneralTab
      ? visibleDocs.length === 0 && docs.length > 0
        ? "Excel documents are listed under All doc."
        : "No shared documents yet. An admin can publish employee docs to this list."
      : "No documents found for the selected filters.";

    return (
      <div className="project-doc-list-panel border border-solid border-[#e7ebf6] rounded-[14px] p-6 bg-white shadow-sm">
        <h2 className="text-[18px] font-semibold text-[#1f2b44] mb-1">{listTitle}</h2>
        <p className="text-sm text-gray-600 mb-4">{listSubtitle}</p>
        <div className="project-doc-filters">
          {showProjectFilter ? (
            <div className="project-doc-filter-field">
              <label className="block text-sm font-semibold text-[#202b44] mb-2">Select project</label>
              <Select
                value={selectedProjectFilter}
                onChange={(value) => setSelectedProjectFilter(value)}
                allowClear
                showSearch
                options={projectOptions}
                placeholder="All projects"
                optionFilterProp="label"
                className="w-full"
              />
            </div>
          ) : null}
          {!isGeneralTab && canUseEmployeeFilter ? (
            <div className="project-doc-filter-field">
              <label className="block text-sm font-semibold text-[#202b44] mb-2">Select employee</label>
              <Select
                value={selectedEmployeeFilter}
                onChange={(value) => setSelectedEmployeeFilter(value)}
                allowClear
                showSearch
                options={employeeOptions}
                placeholder="All employees"
                optionFilterProp="label"
                className="w-full"
              />
            </div>
          ) : null}
        </div>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : visibleDocs.length === 0 ? (
          <p className="text-gray-500">{emptyText}</p>
        ) : (
          <div className="project-doc-list">
            {visibleDocs.map((doc) => (
              <div key={doc.id} className="project-doc-card">
                <div className="project-doc-card__row">
                  <div className="project-doc-card__main">
                    <p className="project-doc-card__meta">Project: {doc.projectName || "-"}</p>
                    <p className="project-doc-card__title">Document title: {doc.title}</p>
                    <p className="project-doc-card__meta text-gray-500">
                      Updated {moment(doc.updatedAt).format("D MMM YYYY, h:mm A")}
                    </p>
                    <div className="project-doc-card__tags">
                      <Tag color={doc.hasFile ? "blue" : "default"}>{getProjectDocUploadTagLabel(doc)}</Tag>
                      {doc.isPublic ? <Tag color="purple">Shared — General doc</Tag> : null}
                      {doc.sharedWithMe ? <Tag color="cyan">Shared with you</Tag> : null}
                      {canShareProjectDoc && doc.sharedWithCount > 0 ? (
                        <Tag color="geekblue">
                          Shared with {doc.sharedWithCount} employee{doc.sharedWithCount === 1 ? "" : "s"}
                        </Tag>
                      ) : null}
                      {isGeneralTab && doc.editAccessStatus ? (
                        <Tag
                          color={
                            doc.editAccessStatus === "approved"
                              ? "green"
                              : doc.editAccessStatus === "pending"
                                ? "gold"
                                : doc.editAccessStatus === "rejected"
                                  ? "red"
                                  : "default"
                          }
                        >
                          Edit access:{" "}
                          {doc.editAccessStatus === "used" ? "used — request again" : doc.editAccessStatus}
                        </Tag>
                      ) : null}
                      {doc.hasFile && doc.fileName ? (
                        <span className="project-doc-card__filename" title={doc.fileName}>
                          {doc.fileName}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="project-doc-card__sidebar">
                    <div className="project-doc-card__actions">
                      {canPreviewProjectDocInList(doc) ? (
                        <Button
                          onClick={() => setListPreviewId((prev) => (prev === doc.id ? null : doc.id))}
                        >
                          {listPreviewId === doc.id ? "Hide preview" : "Preview in list"}
                        </Button>
                      ) : null}
                      <Button
                        icon={<DownloadOutlined />}
                        loading={downloadingDocId === doc.id}
                        onClick={() => onDownloadDocument(doc)}
                      >
                        {doc.hasFile ? "Download file" : "Download PDF"}
                      </Button>
                      <Button type="primary" onClick={() => openDoc(doc.id)}>
                        View
                      </Button>
                      {listRowShowsEditButton(doc) ? (
                        <Button onClick={() => handleListEditClick(doc)}>Edit</Button>
                      ) : null}
                      {canShareProjectDoc ? (
                        <Button icon={<ShareAltOutlined />} onClick={() => openShareModal(doc)}>
                          Share
                        </Button>
                      ) : null}
                      {isAdmin ? (
                        <div className="project-doc-card__public-toggle">
                          <span className="project-doc-card__public-label">Publish to General doc</span>
                          <Switch
                            checked={Boolean(doc.isPublic)}
                            loading={publicToggleLoadingId === doc.id}
                            onChange={(checked) => toggleDocumentPublic(doc, checked)}
                          />
                        </div>
                      ) : null}
                      {showAuthorForDoc(doc) ? (
                        <span className="project-doc-card__author" title="Created by">
                          {doc.authorName || "—"}
                          {doc.authorIsExEmployee ? (
                            <Tag color="gold" className="project-doc-card__ex-author-tag">
                              Ex-employee
                            </Tag>
                          ) : null}
                        </span>
                      ) : null}
                    </div>

                  </div>
                </div>
                {doc.hasFile && listPreviewId === doc.id && isProjectDocPdf(doc) ? (
                  <DocListPdfPreview documentId={doc.id} onLoadError={onListPdfError} />
                ) : null}
                {doc.hasFile && listPreviewId === doc.id && isProjectDocExcel(doc) ? (
                  <DocListExcelPreview documentId={doc.id} onLoadError={onListExcelError} />
                ) : null}
                {doc.hasFile && listPreviewId === doc.id && isProjectDocDocx(doc) ? (
                  <DocListDocxPreview documentId={doc.id} onLoadError={onListDocxError} />
                ) : null}
                {doc.hasFile && listPreviewId === doc.id && isProjectDocMarkdown(doc) ? (
                  <DocListMarkdownPreview documentId={doc.id} onLoadError={onListMarkdownError} />
                ) : null}
                {doc.hasFile && listPreviewId === doc.id && isProjectDocHtml(doc) ? (
                  <DocListHtmlPreview documentId={doc.id} onLoadError={onListMarkdownError} />
                ) : null}
                {doc.hasFile && listPreviewId === doc.id && isProjectDocTextPreview(doc) ? (
                  <DocListTextPreview documentId={doc.id} doc={doc} onLoadError={onListMarkdownError} />
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const segmentedTabs = [
    { key: MAIN_TAB_ALL, label: "All doc" },
    { key: MAIN_TAB_GENERAL, label: "General doc" },
  ];
  if (showAccessRequestsTab) {
    segmentedTabs.push({
      key: MAIN_TAB_ACCESS_REQUESTS,
      label: "Access requests",
      badgeCount: pendingEditRequests.length,
    });
  }

  const renderMainTabPanel = () => {
    if (activeMainTab === MAIN_TAB_ACCESS_REQUESTS) {
      return renderAccessRequestsPanel();
    }
    return renderDocListPanel();
  };

  return (
    <div className="project-documentation-page">
      {!showCreateForm && !viewOpen ? (
        <div className="project-documentation-page__header mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="page-title">Project Documentation</h1>
            <p className="page-subtitle mb-0">Browse project documentation or create a new document.</p>
          </div>
          {canCreateProjectDoc ? (
            <Button type="primary" onClick={() => setShowCreateForm(true)}>
              Create document
            </Button>
          ) : null}
        </div>
      ) : null}

      {canCreateProjectDoc && showCreateForm ? (
        <div className="project-doc-create-shell fixed inset-0 z-[1100] bg-[#eef2f9] overflow-y-auto">
          <div className="min-h-full mx-auto px-4 py-6 sm:px-6">
            <div className="project-doc-create-panel border border-solid border-[#e7ebf6] rounded-[14px] p-6 bg-white shadow-sm min-h-[calc(100vh-3rem)] flex flex-col">
              <div className="project-doc-create-actions flex flex-wrap items-start justify-between gap-3 mb-6">
                <div className="min-w-0 flex-1">
                  <h1 className="page-title mb-1">Create document</h1>
                  <p className="page-subtitle mb-0">
                    Add project name and title, then write the document, upload files (PDF, Word, Markdown, RTF, .txt, .env, .json, .html), or upload
                    one or more Excel sheets.
                  </p>
                </div>
                <Button onClick={closeCreateForm}>Cancel</Button>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-semibold text-[#202b44] mb-2">Project name</label>
                <Input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  maxLength={200}
                  placeholder="e.g. CRM Revamp"
                  className="mb-4"
                />
                <label className="block text-sm font-semibold text-[#202b44] mb-2">Document title</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={200}
                  placeholder="e.g. Sprint handover notes"
                  className="mb-4"
                />

                <label className="block text-sm font-semibold text-[#202b44] mb-2">How do you want to add this document?</label>
                <Radio.Group
                  className="project-doc-radio-group mb-4"
                  value={docInputMode}
                  onChange={(e) => {
                    const next = e.target.value;
                    setDocInputMode(next);
                    if (next === "written" || next !== docInputMode) {
                      clearAttachmentInput();
                    }
                  }}
                >
                  <Radio value="written">Write document content</Radio>
                  <Radio value="upload">Upload files (PDF, Word, Markdown, RTF, .txt, .env, .json, .html)</Radio>
                  <Radio value="excel">Upload Excel sheets (max 15MB each)</Radio>
                </Radio.Group>

                {docInputMode === "written" ? (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-semibold text-[#202b44]">Document content</label>
                      <Button
                        type="text"
                        onClick={() => setIsEditorExpanded((prev) => !prev)}
                        icon={isEditorExpanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                      >
                        {isEditorExpanded ? "Small screen" : "Big screen"}
                      </Button>
                    </div>
                    <div
                      className={`project-doc-editor-wrap mb-4 ${isEditorExpanded ? "project-doc-editor-wrap--expanded fixed inset-4 z-[1200] bg-white rounded-[14px] p-4 shadow-2xl flex flex-col" : ""}`}
                    >
                      {isEditorExpanded ? (
                        <div className="flex justify-between items-center mb-3">
                          <p className="font-semibold text-[#1f2b44]">Document content (big screen)</p>
                          <Button
                            type="text"
                            onClick={() => setIsEditorExpanded(false)}
                            icon={<FullscreenExitOutlined />}
                          >
                            Small screen
                          </Button>
                        </div>
                      ) : null}
                      <div className={isEditorExpanded ? "project-doc-editor-expanded" : ""}>
                        <ReactQuill
                          theme="snow"
                          modules={quillModules}
                          value={content}
                          onChange={setContent}
                          placeholder="Write your project documentation here..."
                          className="text-black"
                          style={{
                            height:
                              docInputMode === "written"
                                ? isEditorExpanded
                                  ? "calc(100vh - 140px)"
                                  : "min(420px, calc(100vh - 340px))"
                                : "200px",
                          }}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-[#202b44] mb-2">
                      {docInputMode === "excel" ? "Excel files" : "Files"}
                    </label>
                    <p className="text-sm text-gray-500 mb-2">
                      {docInputMode === "excel"
                        ? "Select one or more Excel sheets (.xlsx or .xls), up to 15MB each. Drag a file onto another row to set order before upload. They appear as one document in the list; open View to drag tabs and reorder."
                        : "Select one or more files: PDF, Word (.docx), RTF (.rtf), Markdown (.md), plain text (.txt, .env), JSON (.json), HTML (.html), up to 15MB each. Drag a file onto another row to set order before upload. They appear as one document in the list; open View to drag tabs and reorder."}
                    </p>
                    <input
                      ref={attachmentInputRef}
                      type="file"
                      multiple
                      accept={docInputMode === "excel" ? EXCEL_ACCEPT : PROJECT_DOC_UPLOAD_ACCEPT}
                      onChange={onPickAttachment}
                      className="text-sm"
                    />
                    {attachmentFiles.length > 0 ? (
                      <ul className="mt-2 text-sm text-gray-600 list-none pl-0 space-y-2">
                        {attachmentFiles.map((file, index) => {
                          const isDragging = attachmentDragFrom === index;
                          const isDragOver =
                            attachmentDragOver === index && attachmentDragFrom !== index;
                          return (
                            <li
                              key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                              draggable={attachmentFiles.length > 1}
                              className={`project-doc-attachment-row flex flex-wrap items-center justify-between gap-2 border border-[#e7ebf6] rounded-[8px] px-3 py-2 bg-[#fafbff] ${isDragging ? "project-doc-attachment-row--dragging" : ""} ${isDragOver ? "project-doc-attachment-row--drag-over" : ""}`}
                              onDragStart={(e) => {
                                if (attachmentFiles.length < 2) return;
                                e.dataTransfer.effectAllowed = "move";
                                e.dataTransfer.setData(ATTACHMENT_DRAG_INDEX, String(index));
                                setAttachmentDragFrom(index);
                              }}
                              onDragEnd={() => {
                                setAttachmentDragFrom(null);
                                setAttachmentDragOver(null);
                              }}
                              onDragOver={(e) => {
                                if (attachmentDragFrom == null) return;
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "move";
                                if (attachmentDragOver !== index) setAttachmentDragOver(index);
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                const fromIndex = Number(e.dataTransfer.getData(ATTACHMENT_DRAG_INDEX));
                                setAttachmentDragFrom(null);
                                setAttachmentDragOver(null);
                                if (!Number.isFinite(fromIndex) || fromIndex === index) return;
                                reorderAttachmentFile(fromIndex, index);
                              }}
                            >
                              <span className="min-w-0 truncate flex items-center gap-2">
                                {attachmentFiles.length > 1 ? (
                                  <HolderOutlined className="text-gray-400 shrink-0" aria-hidden />
                                ) : null}
                                <span>
                                  <span className="text-gray-400 mr-2">{index + 1}.</span>
                                  {file.name}
                                </span>
                              </span>
                              {attachmentFiles.length > 1 ? (
                                <Space size="small">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<ArrowUpOutlined />}
                                    disabled={index === 0}
                                    onClick={() => moveAttachmentFile(index, -1)}
                                    title="Move up"
                                  />
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<ArrowDownOutlined />}
                                    disabled={index === attachmentFiles.length - 1}
                                    onClick={() => moveAttachmentFile(index, 1)}
                                    title="Move down"
                                  />
                                </Space>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </div>
                )}
              </div>

              <div className="project-doc-create-actions flex flex-wrap gap-3 pt-6 mt-auto border-t border-[#e7ebf6]">
                <Button onClick={closeCreateForm}>Cancel</Button>
                <Button
                  type="primary"
                  onClick={onSubmitDocument}
                  loading={saving}
                  className={docInputMode === "written" ? "mt-0" : ""}
                >
                  Submit document
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {!showCreateForm && !viewOpen ? (
        <div className="project-doc-main-tabs">
          <div className="project-doc-segmented-tabs" role="tablist" aria-label="Project documentation sections">
            {segmentedTabs.map((tab) => {
              const isActive = activeMainTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`project-doc-segmented-tabs__item${isActive ? " is-active" : ""}`}
                  onClick={() => handleMainTabChange(tab.key)}
                >
                  <span className="project-doc-segmented-tabs__label">{tab.label}</span>
                  {tab.badgeCount > 0 ? (
                    <span className="project-doc-segmented-tabs__badge">
                      {tab.badgeCount > 99 ? "99+" : tab.badgeCount}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <div className="project-doc-main-tab-panel" role="tabpanel">
            {renderMainTabPanel()}
          </div>
        </div>
      ) : null}

      {viewOpen ? (
        <div className="project-doc-view-shell fixed inset-0 z-[1100] bg-[#eef2f9] overflow-y-auto">
          <div className="h-auto mx-auto px-4 py-6 sm:px-6">
            <div className="project-doc-view-panel border border-solid border-[#e7ebf6] rounded-[14px] p-6 bg-white shadow-sm min-h-[calc(100vh-3rem)] flex flex-col">
              <div className="project-doc-view-header flex flex-wrap items-start justify-between gap-3 mb-4 shrink-0">
                <div className="min-w-0 flex-1">
                  <h1 className="page-title mb-1 break-words">{selectedDoc?.title || "Document"}</h1>
                  <p className="text-sm text-[#4e5d78] mb-1">
                    <span className="font-semibold">Project:</span> {selectedDoc?.projectName || "-"}
                  </p>
                  <p className="text-xs text-gray-500 mb-0">
                    Updated{" "}
                    {selectedDoc?.updatedAt ? moment(selectedDoc.updatedAt).format("D MMM YYYY, h:mm A") : "-"}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3 shrink-0">
                  {canShareProjectDoc && selectedDoc ? (
                    <Button icon={<ShareAltOutlined />} onClick={() => openShareModal(selectedDoc)}>
                      Share
                    </Button>
                  ) : null}
                  {isAdmin && selectedDoc ? (
                    <div className="project-doc-view-public flex items-center gap-2">
                      <span className="text-sm text-[#4e5d78] whitespace-nowrap">Publish to General doc</span>
                      <Switch
                        checked={Boolean(selectedDoc.isPublic)}
                        loading={publicToggleLoadingId === selectedDoc.id}
                        onChange={(checked) => toggleDocumentPublic(selectedDoc, checked)}
                      />
                    </div>
                  ) : null}
                  <Button onClick={closeModal} className="cursor-pointer">Close</Button>
                </div>
              </div>

              <div className="flex-1 min-h-0">
                {viewLoading ? (
                  <div className="flex justify-center py-16">
                    <Spin size="large" />
                  </div>
                ) : selectedDoc?.hasFile ? (
                  <div className="h-full flex flex-col">
                    {selectedDoc.hasMultipleFiles && Array.isArray(selectedDoc.files) && selectedDoc.files.length > 1 ? (
                      <ProjectDocumentationBundledFilesView
                        documentId={selectedDoc.id}
                        files={selectedDoc.files}
                        onDownloadFile={onDownloadBundledFile}
                        downloadingFileId={downloadingFileId}
                        canReorder={Boolean(selectedDoc.canEdit)}
                        onReorderFiles={onReorderBundledFiles}
                        isReordering={isReorderingFiles}
                        onLoadError={() =>
                          showErrorToast("Could not display a file. Try download or contact support.")
                        }
                      />
                    ) : isProjectDocExcel(selectedDoc) ? (
                      <div className="h-full flex flex-col min-h-0">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2 shrink-0">
                          <p className="text-sm font-semibold text-[#1f2b44] m-0">
                            Excel sheet{selectedDoc.fileName ? `: ${selectedDoc.fileName}` : ""}
                          </p>
                          <Button
                            icon={<DownloadOutlined />}
                            loading={downloadingDocId === selectedDoc.id}
                            onClick={() => onDownloadDocument(selectedDoc)}
                          >
                            Download Excel
                          </Button>
                        </div>
                        <div className="flex-1 min-h-[calc(100vh-220px)] overflow-hidden rounded-[10px]">
                          <ProjectDocumentationExcelViewer
                            documentId={selectedDoc.id}
                            onLoadError={() =>
                              showErrorToast("Could not display the Excel sheet. Try download or contact support.")
                            }
                          />
                        </div>
                      </div>
                    ) : isProjectDocPdf(selectedDoc) ? (
                      <>
                        <p className="text-sm font-semibold text-[#1f2b44] mb-2 shrink-0">PDF</p>
                        {modalPdfLoading ? (
                          <div className="flex justify-center py-16 flex-1">
                            <Spin size="large" />
                          </div>
                        ) : modalPdfUrl ? (
                          <div
                            className="flex-1 min-h-[calc(100vh-220px)] overflow-y-auto bg-[#f5f5f5] rounded-[10px] p-3"
                            onDragStart={(e) => e.preventDefault()}
                          >
                            <CompanyPolicyPdfViewer
                              key={modalPdfUrl}
                              fileUrl={modalPdfUrl}
                              onLoadError={() =>
                                showErrorToast("Could not display the PDF. Try again or contact support.")
                              }
                            />
                          </div>
                        ) : (
                          <p className="text-gray-500">Unable to load PDF.</p>
                        )}
                      </>
                    ) : isProjectDocDocx(selectedDoc) ? (
                      <div className="h-full flex flex-col min-h-0">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2 shrink-0">
                          <p className="text-sm font-semibold text-[#1f2b44] m-0">
                            Word document{selectedDoc.fileName ? `: ${selectedDoc.fileName}` : ""}
                          </p>
                          <Button
                            icon={<DownloadOutlined />}
                            loading={downloadingDocId === selectedDoc.id}
                            onClick={() => onDownloadDocument(selectedDoc)}
                          >
                            Download file
                          </Button>
                        </div>
                        <div className="flex-1 min-h-[calc(100vh-220px)] overflow-hidden rounded-[10px]">
                          <ProjectDocumentationDocxViewer
                            documentId={selectedDoc.id}
                            onLoadError={() =>
                              showErrorToast("Could not display the Word document. Try download or contact support.")
                            }
                          />
                        </div>
                      </div>
                    ) : isProjectDocMarkdown(selectedDoc) ? (
                      <div className="h-full flex flex-col min-h-0">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2 shrink-0">
                          <p className="text-sm font-semibold text-[#1f2b44] m-0">
                            Markdown{selectedDoc.fileName ? `: ${selectedDoc.fileName}` : ""}
                          </p>
                          <Button
                            icon={<DownloadOutlined />}
                            loading={downloadingDocId === selectedDoc.id}
                            onClick={() => onDownloadDocument(selectedDoc)}
                          >
                            Download file
                          </Button>
                        </div>
                        <div className="flex-1 min-h-[calc(100vh-220px)] overflow-hidden rounded-[10px]">
                          <ProjectDocumentationMarkdownViewer
                            documentId={selectedDoc.id}
                            onLoadError={() =>
                              showErrorToast("Could not display the Markdown file. Try download or contact support.")
                            }
                          />
                        </div>
                      </div>
                    ) : isProjectDocHtml(selectedDoc) ? (
                      <div className="h-full flex flex-col min-h-0">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2 shrink-0">
                          <p className="text-sm font-semibold text-[#1f2b44] m-0">
                            HTML file{selectedDoc.fileName ? `: ${selectedDoc.fileName}` : ""}
                          </p>
                          <Button
                            icon={<DownloadOutlined />}
                            loading={downloadingDocId === selectedDoc.id}
                            onClick={() => onDownloadDocument(selectedDoc)}
                          >
                            Download file
                          </Button>
                        </div>
                        <div className="flex-1 min-h-[calc(100vh-220px)] overflow-hidden rounded-[10px]">
                          <ProjectDocumentationHtmlViewer
                            documentId={selectedDoc.id}
                            onLoadError={() =>
                              showErrorToast("Could not display the HTML file. Try download or contact support.")
                            }
                          />
                        </div>
                      </div>
                    ) : isProjectDocTextPreview(selectedDoc) ? (
                      <div className="h-full flex flex-col min-h-0">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2 shrink-0">
                          <p className="text-sm font-semibold text-[#1f2b44] m-0">
                            {isProjectDocJson(selectedDoc) ? "JSON file" : "Text file"}
                            {selectedDoc.fileName ? `: ${selectedDoc.fileName}` : ""}
                          </p>
                          <Button
                            icon={<DownloadOutlined />}
                            loading={downloadingDocId === selectedDoc.id}
                            onClick={() => onDownloadDocument(selectedDoc)}
                          >
                            Download file
                          </Button>
                        </div>
                        <div className="flex-1 min-h-[calc(100vh-220px)] overflow-hidden rounded-[10px]">
                          <ProjectDocumentationTextViewer
                            documentId={selectedDoc.id}
                            formatJson={isProjectDocJson(selectedDoc)}
                            onLoadError={() =>
                              showErrorToast(
                                isProjectDocJson(selectedDoc)
                                  ? "Could not display the JSON file. Try download or contact support."
                                  : "Could not display the text file. Try download or contact support."
                              )
                            }
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-4 py-8">
                        <p className="text-sm font-semibold text-[#1f2b44] m-0">
                          Attached file{selectedDoc.fileName ? `: ${selectedDoc.fileName}` : ""}
                        </p>
                        <Button
                          type="primary"
                          icon={<DownloadOutlined />}
                          loading={downloadingDocId === selectedDoc.id}
                          onClick={() => onDownloadDocument(selectedDoc)}
                        >
                          Download file
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-full flex flex-col min-h-[calc(100vh-220px)]">
                    <p className="font-semibold text-[#1f2b44] mb-2 shrink-0">Content</p>
                    <div
                      className="flex-1 overflow-y-auto text-[#1f2b44] leading-7 ql-editor border border-solid border-[#e7ebf6] rounded-[10px] p-4 bg-[#fafbff]"
                      dangerouslySetInnerHTML={{ __html: selectedDoc?.content || "" }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <Modal
        title="Edit access"
        open={editGuidanceOpen}
        wrapClassName="project-doc-modal-wrap"
        onCancel={() => {
          setEditGuidanceOpen(false);
          setEditGuidanceDoc(null);
        }}
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            {editGuidanceDoc?.editAccessStatus !== "pending" &&
              editGuidanceDoc?.editAccessStatus !== "approved" &&
              isDocAuthor(editGuidanceDoc) &&
              canSubmitProjectDocEditRequest &&
              !canProjectDocUpdate ? (
              <Button type="primary" loading={requestingEdit} onClick={requestEditFromGuidanceModal}>
                Submit edit request
              </Button>
            ) : null}
            <Button
              type={editGuidanceDoc?.editAccessStatus === "pending" ? "primary" : "default"}
              onClick={() => {
                setEditGuidanceOpen(false);
                setEditGuidanceDoc(null);
              }}
            >
              OK
            </Button>
          </div>
        }
        width={480}
      >
        <div className="text-[#1f2b44] text-sm leading-relaxed space-y-3">
          {editGuidanceDoc?.editAccessStatus === "pending" ? (
            <p>
              Your edit access request is waiting for an administrator or approver to review it. Please contact your
              admin if you need it reviewed. After approval, use the <strong>Edit</strong> button beside View to make
              changes.
            </p>
          ) : editGuidanceDoc?.editAccessStatus === "used" ? (
            <p>
              You do not have permission to edit this document. Please contact your <strong>administrator</strong> to request access. Once approval is granted, you can use the <strong>Edit</strong> button next to <strong>View</strong> in the list to open the editor.
            </p>
          ) : (
            <p>
              You do not have permission to edit this document. Please contact your <strong>administrator</strong> to request access. Once approval is granted, you can use the <strong>Edit</strong> button next to <strong>View</strong> in the list to open the editor.
            </p>
          )}
        </div>
      </Modal>

      <Modal
        open={editModalOpen}
        onCancel={closeEditModal}
        footer={null}
        title={editingDoc?.title || "Edit document"}
        width={900}
        wrapClassName="project-doc-modal-wrap"
        destroyOnClose
      >
        {editModalLoading ? (
          <p className="text-gray-500">Loading...</p>
        ) : (
          <div>
            <p className="text-sm text-[#4e5d78] mb-1">
              <span className="font-semibold">Project:</span> {editingDoc?.projectName || "-"}
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Updated{" "}
              {editingDoc?.updatedAt ? moment(editingDoc.updatedAt).format("D MMM YYYY, h:mm A") : "-"}
            </p>

            {editingDoc?.hasFile ? (
              <div>
                {isProjectDocPdf(editingDoc) ? (
                  <>
                    <p className="text-sm font-semibold text-[#1f2b44] mb-2">PDF preview</p>
                    {editModalPdfLoading ? (
                      <div className="flex justify-center py-12">
                        <Spin size="large" />
                      </div>
                    ) : editModalPdfUrl ? (
                      <div
                        className="max-h-[min(40vh,420px)] overflow-y-auto bg-[#f5f5f5] rounded-[10px] p-3 mb-4"
                        onDragStart={(e) => e.preventDefault()}
                      >
                        <CompanyPolicyPdfViewer
                          key={editModalPdfUrl}
                          fileUrl={editModalPdfUrl}
                          onLoadError={() =>
                            showErrorToast("Could not display the PDF. Try again or contact support.")
                          }
                        />
                      </div>
                    ) : (
                      <p className="text-gray-500 mb-4">Unable to load PDF.</p>
                    )}
                  </>
                ) : isProjectDocExcel(editingDoc) ? (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-[#1f2b44] mb-2">Excel preview</p>
                    <ProjectDocumentationExcelViewer
                      documentId={editingDoc.id}
                      onLoadError={() =>
                        showErrorToast("Could not display the Excel sheet. Try download or contact support.")
                      }
                      compact
                    />
                  </div>
                ) : isProjectDocDocx(editingDoc) ? (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-[#1f2b44] mb-2">Word preview</p>
                    <ProjectDocumentationDocxViewer
                      documentId={editingDoc.id}
                      onLoadError={() =>
                        showErrorToast("Could not display the Word document. Try download or contact support.")
                      }
                      compact
                    />
                  </div>
                ) : isProjectDocMarkdown(editingDoc) ? (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-[#1f2b44] mb-2">Markdown preview</p>
                    <ProjectDocumentationMarkdownViewer
                      documentId={editingDoc.id}
                      onLoadError={() =>
                        showErrorToast("Could not display the Markdown file. Try download or contact support.")
                      }
                      compact
                    />
                  </div>
                ) : isProjectDocHtml(editingDoc) ? (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-[#1f2b44] mb-2">HTML preview</p>
                    <ProjectDocumentationHtmlViewer
                      documentId={editingDoc.id}
                      onLoadError={() =>
                        showErrorToast("Could not display the HTML file. Try download or contact support.")
                      }
                      compact
                    />
                  </div>
                ) : isProjectDocTextPreview(editingDoc) ? (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-[#1f2b44] mb-2">
                      {isProjectDocJson(editingDoc) ? "JSON preview" : "Text preview"}
                    </p>
                    <ProjectDocumentationTextViewer
                      documentId={editingDoc.id}
                      formatJson={isProjectDocJson(editingDoc)}
                      onLoadError={() =>
                        showErrorToast(
                          isProjectDocJson(editingDoc)
                            ? "Could not display the JSON file. Try download or contact support."
                            : "Could not display the text file. Try download or contact support."
                        )
                      }
                      compact
                    />
                  </div>
                ) : null}
                <div className="border border-[#e7ebf6] rounded-[10px] p-4 bg-[#fafbff]">
                  <p className="text-sm font-semibold text-[#1f2b44] mb-3">
                    Replace file or update details
                  </p>
                  {editingDoc?.hasMultipleFiles &&
                    Array.isArray(editingDoc.files) &&
                    editingDoc.files.length > 1 ? (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">
                        This document has {editingDoc.files.length} files. Upload a file with the{" "}
                        <strong>same file name</strong> to replace that file.
                      </p>
                      <ul className="text-sm text-gray-600 list-disc pl-5 mb-0 space-y-1">
                        {editingDoc.files.map((file) => (
                          <li key={file.id}>{file.fileName || `File ${file.id}`}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  <label className="block text-sm font-semibold text-[#202b44] mb-2">Project name</label>
                  <Input
                    value={editProjectName}
                    onChange={(e) => setEditProjectName(e.target.value)}
                    maxLength={200}
                    className="mb-3"
                  />
                  <label className="block text-sm font-semibold text-[#202b44] mb-2">Document title</label>
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    maxLength={200}
                    className="mb-3"
                  />
                  <label className="block text-sm font-semibold text-[#202b44] mb-2">New file</label>
                  <p className="text-sm text-gray-500 mb-2">
                    PDF, Word (.docx), RTF (.rtf), Markdown (.md), plain text (.txt, .env), JSON (.json), HTML (.html), or Excel (.xlsx, .xls), up to
                    15MB.
                  </p>
                  <input
                    ref={replaceAttachmentInputRef}
                    type="file"
                    accept={getProjectDocReplaceAccept(editingDoc)}
                    onChange={(e) => {
                      const picked = e.target.files?.[0] || null;
                      if (!picked) {
                        setReplaceAttachmentFile(null);
                        setEditReplaceHint("");
                        return;
                      }
                      const replaceMode = getProjectDocReplaceUploadMode(editingDoc);
                      const fileCheck = validatePickedUploadFile(picked, replaceMode);
                      if (!fileCheck.ok) {
                        showErrorToast(fileCheck.message);
                        setReplaceAttachmentFile(null);
                        setEditReplaceHint("");
                        if (replaceAttachmentInputRef.current) replaceAttachmentInputRef.current.value = "";
                        return;
                      }
                      setReplaceAttachmentFile(picked);
                      const bundleFiles = Array.isArray(editingDoc?.files) ? editingDoc.files : [];
                      const match = findProjectDocFileByName(bundleFiles, picked.name);
                      if (match) {
                        setEditReplaceHint(`Will replace existing file: ${match.fileName}`);
                      } else if (editingDoc?.hasMultipleFiles && bundleFiles.length > 1) {
                        setEditReplaceHint(
                          "No matching file name in this bundle — the main file will be updated."
                        );
                      } else {
                        setEditReplaceHint("");
                      }
                    }}
                    className="text-sm mb-1"
                  />
                  {editReplaceHint ? (
                    <p className="text-sm text-[#4f6ef7] font-medium mb-3">{editReplaceHint}</p>
                  ) : (
                    <div className="mb-3" />
                  )}
                  <Button type="primary" onClick={saveFileReplace} loading={editSaving}>
                    Save changes
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border border-[#e7ebf6] rounded-[10px] p-4 bg-[#fafbff]">
                <p className="text-sm font-semibold text-[#1f2b44] mb-3">Edit document</p>
                <label className="block text-sm font-semibold text-[#202b44] mb-2">Project name</label>
                <Input
                  value={editProjectName}
                  onChange={(e) => setEditProjectName(e.target.value)}
                  maxLength={200}
                  className="mb-3"
                />
                <label className="block text-sm font-semibold text-[#202b44] mb-2">Document title</label>
                <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} maxLength={200} className="mb-3" />
                <label className="block text-sm font-semibold text-[#202b44] mb-2">Document content</label>
                <div className="mb-4">
                  <ReactQuill
                    theme="snow"
                    modules={quillModules}
                    value={editContent}
                    onChange={setEditContent}
                    placeholder="Edit documentation..."
                    className="text-black"
                    style={{ height: "220px" }}
                  />
                </div>
                <Button type="primary" onClick={saveWrittenEdits} loading={editSaving}>
                  Save changes
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={shareModalDoc?.title ? `Share: ${shareModalDoc.title}` : "Share document"}
        open={shareModalOpen}
        onCancel={closeShareModal}
        footer={[
          <Button key="cancel" onClick={closeShareModal}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={shareSaving} onClick={saveDocumentShares}>
            Save sharing
          </Button>,
        ]}
        destroyOnClose
        width={560}
      >
        {shareModalLoading ? (
          <div className="flex justify-center py-10">
            <Spin />
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Assign this document to one or more employees. They will see it in their All doc list with a
              &ldquo;Shared with you&rdquo; tag. The document author is excluded automatically.
            </p>
            <label className="block text-sm font-semibold text-[#202b44] mb-2">Share with employees</label>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Select employees"
              className="w-full"
              value={shareSelectedUserIds}
              onChange={(values) => setShareSelectedUserIds(values)}
              options={shareEmployeeOptions.filter(
                (option) => Number(option.value) !== Number(shareModalDoc?.authorId)
              )}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default ProjectDocumentationPage;
